/**
 * Hook para autenticación de jugadores
 * Maneja login, activación por SMS, y estado de sesión
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { trackLoginJugador, identificarJugador } from '@/lib/analytics';
import { entrarConFaceId } from '@/lib/faceid';

// Canal propio: el interceptor global (hooks/useAuth) pone el admin_token en TODAS las
// consultas; en /me eso daba 401 y cerraba la sesión del jugador si en el mismo navegador
// también había sesión de admin (p. ej. el admin revisando Mi torneo).
const api = axios.create();

const BACKEND_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

interface JugadorSession {
  jugador_id: string;
  jugador_nombre: string;
  telefono: string;
  token: string;
}

interface JugadorAuthContextType {
  jugador: JugadorSession | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (telefono: string, pin: string) => Promise<void>;
  loginFaceId: () => Promise<void>;
  logout: () => void;
  setSession: (session: JugadorSession) => void;
}

const JugadorAuthContext = createContext<JugadorAuthContextType | null>(null);

const STORAGE_KEY = 'jugador_session';

export function JugadorAuthProvider({ children }: { children: ReactNode }) {
  const [jugador, setJugador] = useState<JugadorSession | null>(null);
  const [loading, setLoading] = useState(true);

  // Cargar sesión guardada al inicio
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const session = JSON.parse(stored);
        // Verificar que el token siga válido
        verifyToken(session.token)
          .then((valid) => {
            if (valid) {
              setJugador(session);
              // Registrar visita 1 vez por sesión del navegador
              if (!sessionStorage.getItem('visita_registrada')) {
                sessionStorage.setItem('visita_registrada', '1');
                api.post(`${BACKEND_URL}/api/jugador/visita`, {}, {
                  headers: { Authorization: `Bearer ${session.token}` }
                }).catch(() => {});
              }
            } else {
              localStorage.removeItem(STORAGE_KEY);
            }
          })
          .finally(() => setLoading(false));
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Verificar token con el backend.
  // Solo desloguea con 401/403 explicitos. Otros errores (cold start, red, 500)
  // mantienen la sesion para no desloguear sin razon.
  const verifyToken = async (token: string): Promise<boolean> => {
    try {
      await api.get(`${BACKEND_URL}/api/jugador/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      return true;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) return false;
      // Cold start, timeout, error de red, 500: mantener sesion
      return true;
    }
  };

  // Login con teléfono y PIN
  const login = async (telefono: string, pin: string) => {
    const res = await api.post(`${BACKEND_URL}/api/jugador/login`, {
      telefono,
      pin
    });

    const session: JugadorSession = {
      jugador_id: res.data.jugador_id,
      jugador_nombre: res.data.jugador_nombre,
      telefono,
      token: res.data.access_token
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setJugador(session);
    identificarJugador(session.jugador_id);
    trackLoginJugador();
  };

  // Login con Face ID / huella (lib/faceid): la misma sesión que con el PIN
  const loginFaceId = async () => {
    const r = await entrarConFaceId();
    const session: JugadorSession = {
      jugador_id: r.jugador_id, jugador_nombre: r.jugador_nombre, telefono: r.telefono, token: r.access_token,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setJugador(session);
    identificarJugador(session.jugador_id);
    trackLoginJugador('faceid');
  };

  // Logout
  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setJugador(null);
    identificarJugador(null);
  };

  // Establecer sesión (después de activación)
  const setSession = (session: JugadorSession) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setJugador(session);
  };

  return (
    <JugadorAuthContext.Provider
      value={{
        jugador,
        isAuthenticated: !!jugador,
        loading,
        login,
        loginFaceId,
        logout,
        setSession
      }}
    >
      {children}
    </JugadorAuthContext.Provider>
  );
}

export function useJugadorAuth() {
  const context = useContext(JugadorAuthContext);
  if (!context) {
    throw new Error('useJugadorAuth debe usarse dentro de JugadorAuthProvider');
  }
  return context;
}

// Función helper para obtener headers de autenticación
export function getJugadorAuthHeaders(): Record<string, string> {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const session = JSON.parse(stored);
      return { Authorization: `Bearer ${session.token}` };
    } catch {
      return {};
    }
  }
  return {};
}

export default useJugadorAuth;
